// Quotes list — M2 Phase 5.4 surface. Reads /quotes via crm.ts.
//
// Features:
//   - Search box (debounced 200ms) — searches by quote number or customerNameSnapshot
//   - Status filter chips (All / Lead / Draft / Sent / Follow-up / Negotiating / Won / Lost)
//   - Customer filter (single-select dropdown of all customers)
//   - Imported toggle (off by default — historical bootstrap quotes)
//   - Sortable table (Quote # / Customer / Job Type / Status / Total / Days outstanding)
//   - Row click → /quotes/:id (route doesn't exist yet — stubbed alert)
//   - "+ New quote" button stubbed pending M2 Phase 5.3 quote builder
//   - Empty / loading / error states matched to CustomersPage style

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCustomers, listQuotes, softDeleteQuote } from '@/shared/crm';
import type { Customer, JobType, Quote, QuoteStatus } from '@/shared/crmTypes';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import { NewQuoteModal } from '@/components/NewQuoteModal';

type StatusFilter = QuoteStatus | 'all';

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'lead', label: 'Lead' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'follow_up', label: 'Follow-up' },
  { key: 'negotiating', label: 'Negotiating' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

const JOB_TYPE_LABELS: Record<JobType, { label: string; icon: string }> = {
  multi_part_production: { label: 'Multi-part production', icon: '⚙' },
  prototype: { label: 'Prototype', icon: '💡' },
  one_off: { label: 'One-off', icon: '🔩' },
  quick_quote: { label: 'Quick quote', icon: '⚡' },
  spot_quote: { label: 'Spot quote', icon: '🔍' },
  welding_job: { label: 'Welding job', icon: '🧯' },
  machine_build: { label: 'Machine build', icon: '🏭' },
};

type SortKey = 'number' | 'customer' | 'jobType' | 'status' | 'total' | 'days' | 'updated';
type SortDir = 'asc' | 'desc';

function fmtCurrency(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function daysOutstanding(sentAt?: string): number | null {
  if (!sentAt) return null;
  return Math.floor((Date.now() - new Date(sentAt).getTime()) / (1000 * 60 * 60 * 24));
}

/** Top-tier total: prefer importedTotal for imported quotes, else totalsByTier[0]. */
function quoteTotal(q: Quote): number | undefined {
  if (q.imported && q.importedTotal !== undefined) return q.importedTotal;
  return q.totalsByTier?.[0];
}

/** Whether to render the days-outstanding cell for this quote. */
function showDaysFor(status: QuoteStatus): boolean {
  return status === 'sent' || status === 'follow_up' || status === 'negotiating';
}

/** Color-code the days cell per spec/14 aging dashboard rules. */
function daysCellColor(days: number): string {
  if (days <= 3) return 'text-green';
  if (days <= 7) return 'text-amber';
  if (days <= 14) return 'text-orange';
  return 'text-orange font-bold';
}

export function QuotesPage() {
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [customerFilter, setCustomerFilter] = useState<string>(''); // '' = all
  const [showImported, setShowImported] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [newQuoteOpen, setNewQuoteOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [qList, cList] = await Promise.all([
        // Push status/customerId filtering server-side when set (saves a re-fetch)
        listQuotes({
          status: statusFilter === 'all' ? undefined : statusFilter,
          customerId: customerFilter || undefined,
        }),
        listCustomers(),
      ]);
      setQuotes(qList);
      setCustomers(cList);
    } catch (e) {
      setError(`Failed to load quotes: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, customerFilter]);

  // Debounce search input → searchQuery
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const filteredCustomerName = useMemo(
    () => customers.find((c) => c.id === customerFilter)?.companyName ?? '',
    [customers, customerFilter],
  );

  const filtered = useMemo(() => {
    let out = quotes;
    // Imported toggle: by default hide imported quotes unless user opts in
    if (!showImported) {
      out = out.filter((q) => !q.imported && q.status !== 'imported');
    }
    // Client-side text search across number + customerNameSnapshot
    const needle = searchQuery.trim().toLowerCase();
    if (needle) {
      out = out.filter(
        (q) =>
          q.number.toLowerCase().includes(needle) ||
          (q.customerNameSnapshot ?? '').toLowerCase().includes(needle),
      );
    }
    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...out].sort((a, b) => {
      switch (sortKey) {
        case 'number':
          return a.number.localeCompare(b.number) * dir;
        case 'customer':
          return (a.customerNameSnapshot ?? '').localeCompare(b.customerNameSnapshot ?? '') * dir;
        case 'jobType':
          return a.jobType.localeCompare(b.jobType) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        case 'total': {
          const ta = quoteTotal(a) ?? -Infinity;
          const tb = quoteTotal(b) ?? -Infinity;
          return (ta - tb) * dir;
        }
        case 'days': {
          const da = daysOutstanding(a.sentAt) ?? -Infinity;
          const db = daysOutstanding(b.sentAt) ?? -Infinity;
          return (da - db) * dir;
        }
        case 'updated':
        default:
          return (
            (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * dir
          );
      }
    });
    return sorted;
  }, [quotes, searchQuery, showImported, sortKey, sortDir]);

  // Counts for chips — derived from pre-filter quotes (less imported when toggle off)
  const chipBase = useMemo(
    () => (showImported ? quotes : quotes.filter((q) => !q.imported && q.status !== 'imported')),
    [quotes, showImported],
  );

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'updated' || key === 'total' || key === 'days' ? 'desc' : 'asc');
    }
  }

  function handleRowClick(q: Quote) {
    navigate(`/quotes/${q.id}`);
  }

  function handleNewQuote() {
    setNewQuoteOpen(true);
  }

  function handleCreated(newId: string) {
    // Land the user straight on the new quote so they can start adding parts.
    navigate(`/quotes/${newId}`);
  }

  function clearAllFilters() {
    setSearchInput('');
    setStatusFilter('all');
    setCustomerFilter('');
    setShowImported(false);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-navy">Quotes</h1>
        <button
          type="button"
          onClick={handleNewQuote}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 transition-opacity"
        >
          + New quote
        </button>
      </div>

      {/* Search + filter row */}
      <div className="bg-paper border border-line rounded p-3 mb-4">
        <div className="flex gap-2 items-center mb-3 flex-wrap">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by quote # or customer name…"
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange max-w-[220px]"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-text-mute select-none whitespace-nowrap">
            <input
              type="checkbox"
              checked={showImported}
              onChange={(e) => setShowImported(e.target.checked)}
              className="accent-orange"
            />
            Show imported
          </label>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="text-xs text-text-mute hover:text-orange whitespace-nowrap px-2"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((t) => {
            const active = statusFilter === t.key;
            const count =
              t.key === 'all'
                ? chipBase.length
                : chipBase.filter((q) => q.status === t.key).length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatusFilter(t.key)}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  active
                    ? 'bg-orange text-white'
                    : 'bg-bg text-text-mute border border-line-dark hover:border-orange hover:text-orange'
                }`}
              >
                {t.label}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Active customer filter chip */}
        {customerFilter && (
          <div className="mt-2 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-navy/10 text-navy font-semibold">
              Customer: {filteredCustomerName || '…'}
              <button
                type="button"
                onClick={() => setCustomerFilter('')}
                className="text-navy hover:text-orange ml-1"
                aria-label="Clear customer filter"
              >
                ✕
              </button>
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {loading && quotes.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute">
          <div className="animate-pulse">Loading quotes…</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && quotes.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">📋</div>
          <div className="font-semibold text-navy mb-1">No quotes yet</div>
          <p className="text-sm text-text-mute mb-4">
            The quote builder lands in M2 Phase 5.3. Once it ships, every quote you create will
            show up here with full pipeline visibility.
          </p>
          <button
            type="button"
            onClick={handleNewQuote}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            + Create your first quote
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && quotes.length > 0 && (
        <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
          <div className="text-sm">
            No quotes match{' '}
            {searchQuery.trim() ? (
              <span className="font-mono">"{searchQuery}"</span>
            ) : (
              <span>these filters</span>
            )}
            .
          </div>
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs text-orange font-semibold hover:underline mt-2"
          >
            Clear filters
          </button>
        </div>
      )}

      <NewQuoteModal
        open={newQuoteOpen}
        onClose={() => setNewQuoteOpen(false)}
        onCreated={handleCreated}
      />

      {filtered.length > 0 && (
        <div className="bg-paper border border-line rounded overflow-hidden">
          {/* Header row */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-text-mute">
                <tr>
                  <SortableTh
                    label="Quote #"
                    sortKey="number"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <SortableTh
                    label="Customer"
                    sortKey="customer"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <SortableTh
                    label="Job type"
                    sortKey="jobType"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <SortableTh
                    label="Total (top tier)"
                    sortKey="total"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableTh
                    label="Days out"
                    sortKey="days"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableTh
                    label="Updated"
                    sortKey="updated"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <QuoteRow key={q.id} quote={q} onClick={() => handleRowClick(q)} onDeleted={refresh} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-1.5 text-[11px] text-text-mute border-t border-line bg-bg/40">
            {filtered.length} quote{filtered.length === 1 ? '' : 's'}
            {filtered.length !== chipBase.length && (
              <> (of {chipBase.length} total)</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface SortableThProps {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  className?: string;
}

function SortableTh({ label, sortKey, activeKey, dir, onSort, className }: SortableThProps) {
  const active = activeKey === sortKey;
  const arrow = active ? (dir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      scope="col"
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-orange ${
          active ? 'text-orange' : 'text-text-mute'
        }`}
      >
        <span>{label}</span>
        {arrow && <span className="text-[9px]">{arrow}</span>}
      </button>
    </th>
  );
}

interface QuoteRowProps {
  quote: Quote;
  onClick: () => void;
  onDeleted: () => void;
}

function QuoteRow({ quote, onClick, onDeleted }: QuoteRowProps) {
  // ✖ button per row — confirm + soft-delete. Used to live on the detail
  // page only; Justin wanted faster cleanup from the list.
  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(`Delete quote ${quote.number}?\n\nSoft-deleted (recoverable via Firebase Console).`)) return;
    try {
      await softDeleteQuote(quote.id, 'user-deleted-from-list');
      onDeleted();
    } catch (err) {
      window.alert(`Delete failed: ${(err as Error).message}`);
    }
  }
  const total = quoteTotal(quote);
  const days = daysOutstanding(quote.sentAt);
  const showDays = showDaysFor(quote.status);
  const jobMeta = JOB_TYPE_LABELS[quote.jobType] ?? { label: quote.jobType, icon: '·' };
  const updated = new Date(quote.updatedAt);

  return (
    <tr
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open quote ${quote.number}`}
      className="border-t border-line hover:bg-orange/5 cursor-pointer focus:outline-none focus:bg-orange/10 transition-colors"
    >
      <td className="px-3 py-2 font-mono font-semibold text-navy whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <span>{quote.number}</span>
          {quote.imported && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-navy/10 text-navy font-semibold whitespace-nowrap"
              title="Historical bootstrap quote — total frozen"
            >
              📜 imported
            </span>
          )}
          {quote.marginWarning && (
            <span
              className="text-[10px] text-orange"
              title="Margin below threshold for at least one tier"
              aria-label="Margin warning"
            >
              ⚠
            </span>
          )}
          {quote.approvalRequired && quote.approvalStatus === 'pending' && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber/15 text-amber font-semibold whitespace-nowrap"
              title="Approval required before send"
            >
              approval pending
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 truncate max-w-[220px]" title={quote.customerNameSnapshot}>
        <span className="text-text">{quote.customerNameSnapshot || <em>—</em>}</span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-xs">
          <span aria-hidden>{jobMeta.icon}</span>
          <span className="text-text-mute">{jobMeta.label}</span>
        </span>
      </td>
      <td className="px-3 py-2">
        <QuoteStatusBadge status={quote.status} size="sm" />
      </td>
      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
        {total === undefined ? (
          <span className="text-text-mute">—</span>
        ) : (
          <span className="text-text font-semibold">{fmtCurrency(total)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
        {showDays && days !== null ? (
          <span className={daysCellColor(days)}>{days}d</span>
        ) : (
          <span className="text-text-mute">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-[11px] text-text-mute whitespace-nowrap">
        <time dateTime={quote.updatedAt} title={updated.toLocaleString()}>
          {updated.toLocaleDateString()}
        </time>
      </td>
      <td className="px-2 py-2 w-8">
        <button
          type="button"
          onClick={handleDelete}
          title={`Delete ${quote.number}`}
          aria-label={`Delete quote ${quote.number}`}
          className="text-text-mute hover:text-orange text-sm px-1.5 py-0.5 rounded transition-colors"
        >
          ✖
        </button>
      </td>
    </tr>
  );
}

